import { useState, useMemo } from 'react';
import { useCustomersQuery } from './useCustomersQuery';
import { useDebounce } from '@shared/hooks/useDebounce';
import type { Customer } from '../types/customers.types';

export const useCustomerSearchWithQuery = () => {
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

    // Debounce search term with 500ms delay
    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    // Trim before the minimum-length check so the request matches the API's min:3 validation
    const appliedSearchTerm = useMemo(() => {
        const trimmedSearchTerm = debouncedSearchTerm.trim();
        return trimmedSearchTerm.length >= 3 ? trimmedSearchTerm : '';
    }, [debouncedSearchTerm]);

    const queryParams = useMemo(
        () => ({
            ...(appliedSearchTerm && { search: appliedSearchTerm }),
        }),
        [appliedSearchTerm]
    );

    const {
        data: customers = [] as Customer[],
        isLoading,
        error,
        refetch,
        isFetching,
    } = useCustomersQuery(queryParams);

    const selectCustomer = (customer: Customer) => {
        setSelectedCustomer(customer);
    };

    const clearSelection = () => {
        setSelectedCustomer(null);
    };

    const resetSearch = () => {
        setSearchTerm('');
        setSelectedCustomer(null);
        refetch();
    };

    return {
        customers,
        searchTerm,
        debouncedSearchTerm,
        appliedSearchTerm,
        setSearchTerm,
        selectedCustomer,
        selectCustomer,
        clearSelection,
        resetSearch,
        isLoading,
        isFetching,
        error,
        totalCustomers: Array.isArray(customers) ? customers.length : 0,
        filteredCount: Array.isArray(customers) ? customers.length : 0,
    };
};
